import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Ruler } from "lucide-react";

const TABLES = [
  ["lintels", "Lintels (by clear opening width, m)"],
  ["beams", "Steel beams (by clear span, m)"],
  ["floor_joists", "Floor joists (by clear span, m)"],
  ["flat_roof_joists", "Flat-roof joists (by clear span, m)"],
  ["rafters", "Rafters (by clear span, m)"],
];
const UV = [["wall", "Walls"], ["floor", "Floor"], ["roof_flat", "Flat roof"], ["roof_pitched", "Pitched roof"], ["glazing", "Windows/doors"], ["rooflight", "Rooflights"]];
const INS = [["cavity_mm", "Cavity (mm)"], ["floor_pir_mm", "Floor PIR (mm)"], ["flat_roof_pir_mm", "Flat-roof PIR (mm)"], ["pitched_mw_mm", "Pitched mineral wool (mm)"]];

const inp = "border border-[#E5E7EB] px-2 py-1 text-sm w-full focus:border-[#C79A3B] outline-none";
const lbl = "label-eyebrow block mb-1";

export function BuildingPlanSettings({ settings, setSettings }) {
  const [defs, setDefs] = useState(null);
  const [bp, setBp] = useState(null);
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState(false);
  // Stable per-row IDs so React reconciles form inputs correctly when rows are
  // added/deleted mid-list. Keyed by table name -> parallel array of unique IDs.
  const rowIdsRef = useRef({});
  const rowIdCounter = useRef(0);
  const nextRowId = () => { rowIdCounter.current += 1; return `r${rowIdCounter.current}`; };

  useEffect(() => {
    api.get("/admin/plan-config-defaults").then((r) => {
      setDefs(r.data);
      const stored = settings?.building_plan || {};
      const base = JSON.parse(JSON.stringify(r.data.building_plan_defaults));
      // deep-merge stored over defaults
      const merged = { ...base, ...stored };
      ["u_values", "foundation", "insulation"].forEach((k) => { merged[k] = { ...base[k], ...(stored[k] || {}) }; });
      // Seed a stable id per row in every span table.
      TABLES.forEach(([t]) => { rowIdsRef.current[t] = (merged[t] || []).map(() => nextRowId()); });
      setBp(merged);
      const p = {};
      Object.entries(settings?.material_prices || {}).forEach(([k, v]) => { p[k] = { our: v.our ?? "", market: v.market ?? "" }; });
      setPrices(p);
    }).catch(() => toast.error("Could not load plan settings"));
  }, [settings]);

  if (!bp || !defs) return <div className="text-sm text-[#6B7280]">Loading…</div>;

  const setUV = (k, v) => setBp((b) => ({ ...b, u_values: { ...b.u_values, [k]: v } }));
  const setFnd = (k, v) => setBp((b) => ({ ...b, foundation: { ...b.foundation, [k]: v } }));
  const setIns = (k, v) => setBp((b) => ({ ...b, insulation: { ...b.insulation, [k]: v } }));
  const setCell = (t, i, col, v) => setBp((b) => { const rows = b[t].map((r) => [...r]); rows[i][col] = v; return { ...b, [t]: rows }; });
  const addRow = (t) => {
    rowIdsRef.current[t] = [...(rowIdsRef.current[t] || []), nextRowId()];
    setBp((b) => ({ ...b, [t]: [...b[t], [0, ""]] }));
  };
  const delRow = (t, i) => {
    rowIdsRef.current[t] = (rowIdsRef.current[t] || []).filter((_, idx) => idx !== i);
    setBp((b) => ({ ...b, [t]: b[t].filter((_, idx) => idx !== i) }));
  };
  const setPrice = (k, col, v) => setPrices((p) => ({ ...p, [k]: { ...(p[k] || {}), [col]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      // numeric-coerce span tables; only keep filled price overrides
      const tables = {};
      TABLES.forEach(([t]) => { tables[t] = bp[t].map((r) => [Number(r[0]) || 0, String(r[1] || "")]); });
      const building_plan = {
        u_values: Object.fromEntries(UV.map(([k]) => [k, Number(bp.u_values[k]) || 0])),
        foundation: { depth_mm: Number(bp.foundation.depth_mm) || 0, width_mm: Number(bp.foundation.width_mm) || 0, concrete: bp.foundation.concrete || "" },
        insulation: Object.fromEntries(INS.map(([k]) => [k, Number(bp.insulation[k]) || 0])),
        ...tables,
      };
      const material_prices = {};
      Object.entries(prices).forEach(([k, v]) => {
        const o = {};
        if (v.our !== "" && v.our != null) o.our = Number(v.our);
        if (v.market !== "" && v.market != null) o.market = Number(v.market);
        if (Object.keys(o).length) material_prices[k] = o;
      });
      const r = await api.put("/admin/settings", { building_plan, material_prices });
      setSettings(r.data);
      toast.success("Building plan & materials settings saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const grouped = {};
  defs.material_price_defs.forEach((d) => { (grouped[d.section] = grouped[d.section] || []).push(d); });

  return (
    <div className="mt-12 pt-8 border-t border-[#E5E7EB]" data-testid="settings-building-plan">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h2 className="text-xl font-black tracking-tight flex items-center gap-2"><Ruler size={18} /> Building plan &amp; materials</h2>
        <button data-testid="bp-save" onClick={save} disabled={saving} className="de-btn-primary text-xs inline-flex items-center gap-1.5"><Save size={14} /> {saving ? "Saving…" : "Save changes"}</button>
      </div>
      <p className="text-sm text-[#6B7280] mb-6">Tune the engineering assumptions used in the Building Control plan and the generic prices used in the materials estimate.</p>

      {/* U-values */}
      <h3 className="font-bold text-[#12263A] mb-2">Target U-values (W/m²K)</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        {UV.map(([k, label]) => (
          <div key={k}><label className={lbl}>{label}</label>
            <input data-testid={`bp-uvalue-${k}`} className={inp} value={bp.u_values[k] ?? ""} onChange={(e) => setUV(k, e.target.value)} /></div>
        ))}
      </div>

      {/* Foundation + insulation */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="font-bold text-[#12263A] mb-2">Foundation</h3>
          <div className="space-y-2">
            <div><label className={lbl}>Min depth (mm)</label><input data-testid="bp-fnd-depth" className={inp} value={bp.foundation.depth_mm ?? ""} onChange={(e) => setFnd("depth_mm", e.target.value)} /></div>
            <div><label className={lbl}>Width (mm)</label><input data-testid="bp-fnd-width" className={inp} value={bp.foundation.width_mm ?? ""} onChange={(e) => setFnd("width_mm", e.target.value)} /></div>
            <div><label className={lbl}>Concrete grade</label><input data-testid="bp-fnd-concrete" className={inp} value={bp.foundation.concrete ?? ""} onChange={(e) => setFnd("concrete", e.target.value)} /></div>
          </div>
        </div>
        <div>
          <h3 className="font-bold text-[#12263A] mb-2">Insulation thickness</h3>
          <div className="grid grid-cols-2 gap-2">
            {INS.map(([k, label]) => (
              <div key={k}><label className={lbl}>{label}</label><input data-testid={`bp-ins-${k}`} className={inp} value={bp.insulation[k] ?? ""} onChange={(e) => setIns(k, e.target.value)} /></div>
            ))}
          </div>
        </div>
      </div>

      {/* Span tables */}
      <h3 className="font-bold text-[#12263A] mb-2">Indicative span tables</h3>
      <p className="text-xs text-[#6B7280] mb-3">Each row: if the span/width is ≤ the value (m), the spec on the right is used. Keep one high "catch-all" row (e.g. 999) for the engineer fallback.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {TABLES.map(([t, title]) => (
          <div key={t} data-testid={`bp-table-${t}`} className="border border-[#E5E7EB] p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-widest text-[#12263A]">{title}</div>
              <button onClick={() => addRow(t)} className="text-[#C79A3B] hover:text-[#A87E28]" title="Add row"><Plus size={16} /></button>
            </div>
            <div className="space-y-1.5">
              {bp[t].map((row, i) => (
                <div key={(rowIdsRef.current[t] || [])[i] || `fallback-${i}`} className="flex items-center gap-2">
                  <input className={`${inp} w-16 flex-shrink-0`} value={row[0]} onChange={(e) => setCell(t, i, 0, e.target.value)} />
                  <input className={inp} value={row[1]} onChange={(e) => setCell(t, i, 1, e.target.value)} />
                  <button onClick={() => delRow(t, i)} className="text-[#9CA3AF] hover:text-red-500 flex-shrink-0" title="Remove"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Material price book */}
      <h3 className="font-bold text-[#12263A] mb-2">Generic material prices (£)</h3>
      <p className="text-xs text-[#6B7280] mb-3">Leave blank to use the default shown in the placeholder. "Our" is your trade cost; "Market" is the retail comparison.</p>
      <div className="space-y-4">
        {Object.entries(grouped).map(([section, items]) => (
          <div key={section} className="border border-[#E5E7EB]">
            <div className="bg-[#F8F6F2] px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-[#12263A]">{section}</div>
            <div className="divide-y divide-[#F0F1F3]">
              {items.map((d) => (
                <div key={d.key} className="flex items-center gap-3 px-3 py-1.5">
                  <div className="flex-1 text-sm text-[#12263A]">{d.label} <span className="text-[11px] text-[#9CA3AF]">/ {d.unit}</span></div>
                  <input data-testid={`bp-price-${d.key}-our`} className={`${inp} w-24`} placeholder={`${d.our}`} value={prices[d.key]?.our ?? ""} onChange={(e) => setPrice(d.key, "our", e.target.value)} />
                  <input data-testid={`bp-price-${d.key}-market`} className={`${inp} w-24`} placeholder={`${d.market}`} value={prices[d.key]?.market ?? ""} onChange={(e) => setPrice(d.key, "market", e.target.value)} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <button data-testid="bp-save-2" onClick={save} disabled={saving} className="de-btn-primary text-xs inline-flex items-center gap-1.5"><Save size={14} /> {saving ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
}
